import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import ProgressiveControl from "../common/ProgressiveControl";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import FieldNote from "./common/FieldNote";
import WebsiteMediaInput from "./WebsiteMediaInput";

export default function WebsiteGalleryCategories() {
    const { requestAPI, showToast } = useAppContext();
    const navigate = useNavigate();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [rows, setRows] = useState();
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/gallery-categories",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRows(data);
                else setError("Couldn't load Gallery Folders");
            },
        });
    }, [requestAPI]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Gallery folders" />
            <TabHeader
                className="px-3 pt-3"
                title="Gallery folders"
                highlights={["A folder is the first card on the Gallery page, such as Events", "Open a folder to add the events inside it"]}
                actionItems={[
                    <Button icon="pi pi-plus" severity="warning" onClick={() => setDialog({ visible: true, record: {} })} />,
                ]}
            />
            <Divider />
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className="flex-1 overflow-y-scroll flex flex-column gap-2 p-2">
                    {rows?.length ? rows.map((row) => (
                        <Row key={row.id} row={row} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? <Editor dialog={dialog} setDialog={setDialog} setRows={setRows} requestAPI={requestAPI} showToast={showToast} /> : null}
        </div>
    );
}

function Row({ row, setRows, requestAPI, showToast, openEdit }) {
    const navigate = useNavigate();
    const [deleting, setDeleting] = useState();
    const remove = () => {
        requestAPI({
            requestPath: `website/gallery-categories/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Gallery Folders Deleted", life: 1000 });
                    setRows((prev) => prev?.filter((item) => item.id !== row.id));
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
                }
            },
        });
    };
    const active = row.is_published;
    return (
        <div className={`flex gap-3 align-items-center border-round py-2 px-3 text-white ${active ? "bg-green-500" : "bg-red-500"}`}>
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{row.id}. {row.title || "Untitled"}</span>
            </div>
            <i className="pi pi-folder-open cursor-pointer" title="Open folder" onClick={() => navigate(`/manage-website/gallery/folders/${row.slug || row.public_id}`)} />
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function Editor({ dialog, setDialog, setRows, requestAPI, showToast }) {
    const [record, setRecord] = useState({ is_published: true, category_type: "albums", ...(dialog.record || {}) });
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const save = () => {
        requestAPI({
            requestPath: "website/gallery-categories",
            requestMethod: isNew ? "POST" : "PATCH",
            requestPostBody: record,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 }),
            onResponseReceieved: (saved, responseCode) => {
                if (saved && (responseCode === 200 || responseCode === 201)) {
                    setRows((prev) => (isNew ? [...(prev || []), saved] : prev.map((item) => (item.id === saved.id ? saved : item))));
                    showToast({ severity: "success", summary: "Saved", detail: "Gallery Folders Saved", life: 1000 });
                    setDialog({ visible: false });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: saved?.error || "Failed To Save !", life: 2000 });
                }
            },
        });
    };
    return (
        <Dialog header={isNew ? "Add a folder" : "Edit folder"} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="title">Folder name</label>
                        </FloatLabel>
                        <FieldNote text="Shown on the Gallery page. Example: Events." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputTextarea id="description" className="w-full" rows={4} autoResize value={record?.description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} disabled={saving} />
                            <label htmlFor="description">Line under the folder name</label>
                        </FloatLabel>
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <Dropdown inputId="category_type" className="w-full" options={[{ label: "Events", value: "albums" }, { label: "People", value: "people" }]} optionLabel="label" optionValue="value" value={record?.category_type || "albums"} onChange={(event) => setRecord((prev) => ({ ...prev, category_type: event.value }))} disabled={saving} />
                            <label htmlFor="category_type">What is inside this folder</label>
                        </FloatLabel>
                        <FieldNote text="Events holds occasions such as Diwali. People holds staff or founder profiles." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="icon" className="w-full" value={record?.icon ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, icon: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="icon">Icon name</label>
                        </FloatLabel>
                        <FieldNote text="Optional Material icon name, such as celebration. A cover photo replaces it." />
                    </div>
                    <WebsiteMediaInput label="Folder cover" field="cover_image" hint="Required size: 1280 ? 800 px (16:10). A larger photo is scaled to fit this folder card. The whole photo stays visible." record={record} setRecord={setRecord} disabled={saving} />
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="cover_alt" className="w-full" value={record?.cover_alt ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, cover_alt: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="cover_alt">Cover description</label>
                        </FloatLabel>
                        <FieldNote text="Read by screen readers. Describe the cover photo." />
                    </div>
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="is_published" checked={record?.is_published !== false} onChange={(event) => setRecord((prev) => ({ ...prev, is_published: event.checked }))} disabled={saving} />
                        <label htmlFor="is_published" className={TEXT_SMALL}>Show this folder on the website</label>
                    </div>
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}
